import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Edit from '@mui/icons-material/Edit';
import Archive from '@mui/icons-material/Archive';
import CheckCircleOutlined from '@mui/icons-material/CheckCircleOutlined';
import Block from '@mui/icons-material/Block';
import Unarchive from '@mui/icons-material/Unarchive';
import PersonAdd from '@mui/icons-material/PersonAdd';
import { getAll, archiveUser } from '../../services/UserService';
import DialogModal from '../../utils/DialogModal';
import { withSnackbar } from '../../utils/SnackbarHOC';
import { Box, Button, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import { commonGridSx, gridContainerSx, gridLocaleText, makeGridToolbar } from '../../utils/gridStyles';
import { GridToolbarContainer, GridToolbarColumnsButton, GridToolbarFilterButton, GridToolbarDensitySelector, GridToolbarQuickFilter } from '@mui/x-data-grid';
import { DataGrid } from '../../components/SortableDataGrid/SortableDataGrid';

const AddUserButton = ({ onClick }) => (
  <Button startIcon={<PersonAdd sx={{ fontSize: 16 }} />} onClick={onClick}
    sx={{ textTransform: 'none', fontWeight: 500, fontSize: '1rem', color: '#03407b', border: '1px solid #aed6f1', borderRadius: 2, px: 2, py: 0.75, background: '#ebf4fb', '&:hover': { background: '#d6eaf8', borderColor: '#7fb3d3' } }}>
    Ajouter un utilisateur
  </Button>
);

const Listeruser = ({ snackbarShowMessage }) => {
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [loadingAction, setLoadingAction] = useState(false);
  const [reloadData, setReloadData] = useState(false);

  useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        const response = await getAll();
        setUsers(response.data.users);
      } catch {
        snackbarShowMessage('Erreur de chargement des utilisateurs.', 'error', 4000);
      } finally {
        setLoading(false);
      }
    })();
  }, [reloadData]);

  const handleArchiverUser = (data, handleClose) => {
    setLoadingAction(true);
    archiveUser(data)
      .then(() => {
        snackbarShowMessage(`L'utilisateur a été ${data.status ? 'activé' : 'désactivé'} avec succès`, 'success', 4000);
        setReloadData(!reloadData);
        handleClose();
      })
      .catch(() => {
        snackbarShowMessage(`Erreur ${data.status ? "d'activage" : "de désactivage"} de l'utilisateur.`, 'error', 4000);
      })
      .finally(() => setLoadingAction(false));
  };

  const CustomToolbar = () => (
    <GridToolbarContainer sx={{ px: 2, py: 1.25, borderBottom: '1px solid #f1f5f9', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
      <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#0f172a', mr: 'auto' }}>Utilisateurs</Typography>
      <AddUserButton onClick={() => history.push('/users/add')} />
      <GridToolbarQuickFilter
        sx={{
          '& .MuiInputBase-root': { borderRadius: 2, fontSize: '1rem', background: '#f8fafc', px: 1.5, height: 36 },
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' },
          '& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#7fb3d3' },
          '& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#03407b' },
        }}
        placeholder="Rechercher..."
        debounceMs={400}
      />
      <GridToolbarColumnsButton sx={{ textTransform: 'none', fontSize: '1rem', color: '#64748b', borderRadius: 2 }} />
      <GridToolbarFilterButton sx={{ textTransform: 'none', fontSize: '1rem', color: '#64748b', borderRadius: 2 }} />
      <GridToolbarDensitySelector sx={{ textTransform: 'none', fontSize: '1rem', color: '#64748b', borderRadius: 2 }} />
    </GridToolbarContainer>
  );

  const columns = [
    {
      field: 'prenom', headerName: 'PRÉNOM', flex: 0.9, minWidth: 120,
      renderCell: (p) => (
        <Typography sx={{ fontSize: '1rem', color: '#334155', textTransform: 'capitalize', fontWeight: 500 }}>{p.value || '—'}</Typography>
      ),
    },
    {
      field: 'nom', headerName: 'NOM', flex: 0.9, minWidth: 120,
      renderCell: (p) => (
        <Typography sx={{ fontSize: '1rem', color: '#0f172a', textTransform: 'uppercase', fontWeight: 600 }}>{p.value || '—'}</Typography>
      ),
    },
    {
      field: 'localisation', headerName: 'LOCALISATION', flex: 0.8, minWidth: 110,
      renderCell: (p) => <Typography sx={{ fontSize: '1rem', color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.value || '—'}</Typography>,
    },
    {
      field: 'fonction', headerName: 'FONCTION', flex: 1, minWidth: 140,
      renderCell: (p) => <Typography sx={{ fontSize: '1rem', color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.value || '—'}</Typography>,
    },
    {
      field: 'tel_mobile', headerName: 'TÉL MOBILE', flex: 0.8, minWidth: 120,
      renderCell: (p) => <Typography sx={{ fontSize: '1rem', color: '#334155' }}>{p.value || '—'}</Typography>,
    },
    {
      field: 'email', headerName: 'EMAIL', flex: 1.3, minWidth: 180,
      renderCell: (p) => (
        <Typography sx={{ fontSize: '1rem', color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.value || '—'}</Typography>
      ),
    },
    {
      field: 'role', headerName: 'RÔLE', flex: 1.1, minWidth: 160,
      renderCell: (p) => (
        <Chip
          label={p.value?.replace('ROLE_', '').replace(/_/g, ' ')}
          size="small"
          sx={{ background: '#ebf4fb', color: '#02357a', fontWeight: 600, fontSize: '0.7rem', border: '1px solid #aed6f1' }}
        />
      ),
    },
    {
      field: 'isActive', headerName: 'ÉTAT', width: 80, align: 'center', headerAlign: 'center',
      renderCell: (p) => p.value
        ? <CheckCircleOutlined sx={{ color: '#16a34a', fontSize: 20 }} />
        : <Block sx={{ color: '#dc2626', fontSize: 20 }} />,
    },
    {
      field: 'actions_col', headerName: 'ACTIONS', width: 130, sortable: false, filterable: false,
      renderCell: (p) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title="Modifier">
            <IconButton size="small" onClick={() => history.push(`/users/${p.row.uuid}/update`)}
              sx={{ color: '#eab308', background: '#fefce8', borderRadius: '8px', '&:hover': { background: '#fef9c3' }, p: 0.75 }}>
              <Edit sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={p.row.isActive ? "Désactiver" : "Activer"}>
            <span>
              <DialogModal
                label={
                  <IconButton size="small"
                    sx={{ color: p.row.isActive ? '#ef4444' : '#16a34a', background: p.row.isActive ? '#fef2f2' : '#f0fdf4', borderRadius: '8px', '&:hover': { background: p.row.isActive ? '#fee2e2' : '#dcfce7' }, p: 0.75 }}>
                    {p.row.isActive ? <Archive sx={{ fontSize: 16 }} /> : <Unarchive sx={{ fontSize: 16 }} />}
                  </IconButton>
                }
                description={`Voulez vous ${p.row.isActive ? 'désactiver' : 'activer'} l'utilisateur ?`}
                title={`${p.row.isActive ? 'Désactiver' : 'Activer'} l'utilisateur ?`}
                closeLabel="Annuler"
                confirmLabel={p.row.isActive ? 'Désactiver' : 'Activer'}
                loading={loadingAction}
                onConfirm={handleArchiverUser}
                data={{ user: p.row.uuid, status: !p.row.isActive }}
              />
            </span>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const rows = users.map((u) => ({ ...u, id: u.uuid || u.id }));

  return (
    <Box sx={{ px: '20px', pb: 2, backgroundColor: '#f1f5f9', minHeight: '100vh' }}>
      <Typography sx={{ pt: 2, pb: 1.5, color: '#0f172a', fontFamily: "'Fira Sans', sans-serif", fontWeight: 700, fontSize: '1.5rem', letterSpacing: '-0.01em' }}>
        UTILISATEURS
      </Typography>
      <Box sx={{ ...gridContainerSx, height: 'calc(100vh - 110px)' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.uuid || row.id}
          loading={loading}
          pageSizeOptions={[50, 100]}
          disableRowSelectionOnClick
          showToolbar
          slots={{ toolbar: CustomToolbar }}
          slotProps={{ loadingOverlay: { variant: 'skeleton', noRowsVariant: 'skeleton' } }}
          localeText={gridLocaleText}
          sx={commonGridSx}
        />
      </Box>
    </Box>
  );
};

export default withSnackbar(Listeruser);
